import { useInfiniteQuery } from "@tanstack/react-query";
import { fetchClient } from "@/lib/api-client";

interface UseFetchSearchProductsParams {
  search?: string;
  category_id?: string;
  parent_category_id?: string;
  limit?: number;
  enabled?: boolean;
}

export const useFetchSearchProducts = (
  params: UseFetchSearchProductsParams = {},
) => {
  const {
    search,
    category_id,
    parent_category_id,
    limit = 10,
    enabled = true,
  } = params;

  const queryFn = async ({ pageParam = 1 }) => {
    const response = await fetchClient.GET("/product/search", {
      params: {
        query: {
          page: typeof pageParam === "number" ? pageParam : 1,
          limit,
          search: search || undefined,
          category_id: category_id || undefined,
          parent_category_id: parent_category_id || undefined,
        },
      },
    });

    const error = response.error;
    if (error) throw error;
    return response.data;
  };

  const {
    data,
    error,
    isLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isFetching,
    isFetchNextPageError,
  } = useInfiniteQuery({
    queryKey: [
      "search-products",
      search,
      category_id,
      parent_category_id,
      limit,
    ],
    queryFn,
    initialPageParam: 1,
    getNextPageParam: (lastPage) => {
      const pagination = lastPage?.data?.pagination;
      if (pagination?.hasNextPage) return pagination.page + 1;
      return undefined;
    },
    enabled: enabled && (!!search || !!category_id || !!parent_category_id),
    refetchOnReconnect: false,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchInterval: false,
    refetchIntervalInBackground: false,
  });

  return {
    data,
    error,
    isLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isFetching,
    isFetchNextPageError,
  };
};
